import { memo, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import {
  createSearchParams,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import { getRequest } from '../../../../../services/base'
import PinRow from './PinRow'
import SearchConnectorsButton from './SearchConnectorsButton'
import { ConnectorPinsContext } from '../context/context'
import { NavContext } from '../../../../../context/global_context'
import { notifyError } from '../../../../../utils/notifications'
import {
  Col,
  Empty,
  Flex,
  Form,
  List,
  Row,
  Space,
  Tabs,
  Typography,
} from 'antd'
import Button from '../../../../common/Button'
import IconStartConnector from '../../../../icons/IconStartConnector'
import IconEndConnector from '../../../../icons/IconEndConnector'
import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons'
import EllipsisTooltip from '../../../../common/EllipsisTooltip'
import useDebouncedValue from '../../../../../utils/common_function'
import InputText from '../../../../common/InputText'

export const TabContent = memo(({ destinationOnly = false, hasSelectedDtc = false }) => {
  const intl = useIntl()
  let { vehicle, connector_id } = useParams()
  const [navState, navDispatch] = useContext(NavContext)
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const [form] = Form.useForm()

  const [connectorData, setConnectorData] = useState({})
  const [connectorPins, setConnectorPins] = useState([])
  const [sortOrder, setSortOrder] = useState(null)
  const [searchValue, setSearchValue] = useState(null)
  const debouncedSearch = useDebouncedValue(searchValue, 500)

  const searchPlaceholder = intl.formatMessage({ id: 'dtcs.connectorMapping.searchPins' })
  const pinsNotAvailable = intl.formatMessage({ id: 'dtcs.connectorMapping.pinsNotAvailable' })

  const getConnectorDetails = async () => {
    let res = await getRequest(
      `/api/connector/${connector_id}/?vehicle=${vehicle}`
    )
    // Without this the page simply renders nothing: `res?.id` is undefined and
    // getConnectorPins() below is never reached.
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'dtcs.connectorMapping.connectorLoadError' }))
      return
    }
    if (res?.id) {
      setConnectorData(res)
      getConnectorPins()
    }
  }

  const getConnectorPins = async () => {
    let res = await getRequest(
      `/api/dtcs/connector_pins/?connector=${searchParams.get(
        'connector_name'
      )}&vehicle=${vehicle}`
    )
    // The length check below leaves a failure completely silent, so the failure
    // branch has to be explicit: base.js returns {status: false}, not a rejection.
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'dtcs.connectorMapping.pinsLoadError' }))
      return
    }
    if (res?.data?.length > 0) {
      setConnectorPins(res?.data)
    }
  }

  const goToMapping = () => {
    navigate({
      pathname: `/vehicles/${vehicle}/dtcs/listing/mappings`,
      search: `?${createSearchParams({
        dtc_id: searchParams.getAll('dtc_id'),
        dtc_mapping_type: searchParams.get('dtc_mapping_type'),
        ...searchParams.has('start_q') ? {start_q: searchParams.get('start_q')} : {},
        ...searchParams.has('end_q') ? {end_q: searchParams.get('end_q')} : {},
      })}`,
    })
  }

  useEffect(() => {
    if (hasSelectedDtc) {
      getConnectorDetails()
    }
  }, [hasSelectedDtc, connector_id, vehicle])

  /* Filter "Pins" based on search term */
  const filterPins = useCallback((data, search) => {
    if (!search) return data
    
    return data
      .map((connector) => {
        const content = `${connector.circuit_name} ${connector.circuit_desc} ${connector.circuit_id}`
        const match = content.toLowerCase().includes(search.toLowerCase())

        if (match) return connector

        return null
      })
      .filter(Boolean)
  }, [])

  /* Update "filteredPins" used to cerate "Pins List" when "debouncedSearch" value changes */
  const filteredPins = useMemo(() => {
    return filterPins(connectorPins, debouncedSearch)
  }, [connectorPins, debouncedSearch, filterPins])

  // Sort Pins
  const sortedData = useMemo(() => {
    if (!sortOrder) return filteredPins
    
    return [...filteredPins].sort((a, b) => {
      return sortOrder === 'asc'
        ? a.circuit_name.localeCompare(b.circuit_name)
        : b.circuit_name.localeCompare(a.circuit_name)
    })
  }, [filteredPins, sortOrder])

  const toggleSort = () => {
    setSortOrder((prev) => {
      switch (prev) {
        case null:
          return 'asc'
        case 'asc':
          return 'desc'
        default:
          return null
      }
    })
  }

  const ListHeader = () => {
    return (
      <div className="pins-header">
        <Flex
          align="center"
          gap={8}
          className="sorting-column-header"
          onClick={toggleSort}
        >
          <Typography.Text className="semi-bold">{intl.formatMessage({ id: 'dtcs.connectorMapping.pins' })}</Typography.Text>

          <Flex vertical gap={0} className="sort-icon">
            <CaretUpOutlined className={sortOrder === 'asc' ? 'active' : ''} />
            <CaretDownOutlined
              className={sortOrder === 'desc' ? 'active' : ''}
            />
          </Flex>
        </Flex>
      </div>
    )
  }

  return (
    <Form
      form={form}
      layout="vertical"
      className="col-body"
    >
      <Flex
        direction="horizontal"
        justify="space-between"
        gap={16}
        className="selected-connector"
      >
        <Space direction="vertical" size={0}>
          <p className="bolder" style={{wordBreak: 'break-word'}}>{connectorData?.name}</p>
          <p style={{wordBreak: 'break-word'}}>{connectorData?.description}</p>
        </Space>

        <Button
          type="link"
          className="link-button"
          onClick={() => goToMapping()}
        >
          {intl.formatMessage({ id: 'dtcs.connectorMapping.clear' })}
        </Button>
      </Flex>

      {!destinationOnly ? (
        connectorPins?.length > 0 && connectorData?.name ? (
          <>
            {connectorPins?.length > 1 && (
              <InputText
                type="search"
                name="search_connector"
                label={<span className='semi-bold'>{searchPlaceholder}</span>}
                placeholder='Search'
                value={searchValue}
                hideLabel={false}
                isRequired={false}
                wrapperClass="search-pins"
                disabled={connectorPins.length < 2 ? true : false}
                onChange={(value) => {
                  setSearchValue(value)
                }}
              />
            )}

            <Flex vertical justify="space-between" className="pins-section">
              <ListHeader />

              <div className="pins-list-container">
                <List
                  bordered
                  dataSource={sortedData}
                  className="pins-list"
                  renderItem={(pin, index) => {
                    return (
                      <List.Item key={`${pin.pin_from}-${pin.circuit_id}`} data-testid="dtc-pin">
                        <PinRow
                          name={`${pin.pin_from}/${pin.circuit_name}`}
                          value={`${pin.pin_from}/${pin.circuit_name}`}
                          description={pin.circuit_desc}
                          harnessName=""
                          circuitId={pin.circuit_id}
                          inputName={`pins_${index}-${pin.pin_from}_${pin.circuit_name}-${pin.circuit_id}`}
                          key={`pin-${index}`}
                          pin={pin.pin_from}
                          circuit_name={pin.circuit_name}
                          formRef={form}
                        />
                      </List.Item>
                    )
                  }}
                />
              </div>
            </Flex>

            <SearchConnectorsButton />
          </>
        ) : (
          <Empty description={pinsNotAvailable} className='no-pins-available' />
        )
      ) : (
        <></>
      )}
    </Form>
  )
})


const ConnectorPinsSection = memo(({ destinationOnly = false, hasSelectedDtc = false }) => {
  const intl = useIntl()
  const [searchParams, setSearchParams] = useSearchParams()
  const [state, dispatch] = useContext(ConnectorPinsContext)
  const [dtcMappingType, setDtcMappingType] = useState(null)
  
  const tabItems = useMemo(() => [
    {
      key: 'start',
      icon: <IconStartConnector />,
      label: (
        <EllipsisTooltip className="tab-label">{intl.formatMessage({ id: 'dtcs.connectorMapping.startConnector' })}</EllipsisTooltip>
      ),
      children: <TabContent destinationOnly={destinationOnly} hasSelectedDtc={hasSelectedDtc} />,
    },
    {
      key: 'end',
      icon: <IconEndConnector />,
      label: (
        <EllipsisTooltip className="tab-label">{intl.formatMessage({ id: 'dtcs.connectorMapping.endConnector' })}</EllipsisTooltip>
      ),
      children: <TabContent destinationOnly={destinationOnly} hasSelectedDtc={hasSelectedDtc} />,
    },
  ], [destinationOnly, hasSelectedDtc])

  useEffect(() => {
    if(!dtcMappingType) return

    const params = new URLSearchParams(searchParams)
    params.set('dtc_mapping_type', dtcMappingType)
    setSearchParams(params);
    (destinationOnly) ? (
      dispatch({ type: 'dtc_mapping_type', data: 'end' })
    ) : (
      dispatch({ type: 'dtc_mapping_type', data: dtcMappingType })
    )
  }, [dtcMappingType])

  useEffect(() => {
    let value = (destinationOnly) ? (
      'start'
    ) : (
      searchParams.get('dtc_mapping_type') ?? 'end'
    )

    setDtcMappingType(value)
  }, [])

  return (
    <>
      {!destinationOnly ? (
        <Flex className="col" gap={16}>
          <Tabs
            items={tabItems}
            activeKey={dtcMappingType}
            centered={true}
            tabBarGutter={0}
            className="connector-tabs"
            animated={false}
            onChange={(value) => {
              setDtcMappingType(value)
            }}
          />
        </Flex>
      ) : (
        <Flex vertical gap={16} className='col'>
          <Typography.Title level={5} className="col-header">
            <Typography.Text className="icon non-interactive">
              <IconStartConnector />
            </Typography.Text>
            <Typography.Text className="title non-interactive">
              {intl.formatMessage({ id: 'dtcs.connectorMapping.startConnector' })}
            </Typography.Text>
          </Typography.Title>

          <TabContent destinationOnly={destinationOnly} hasSelectedDtc={hasSelectedDtc} />
        </Flex>
      )}
    </>
  )
})

export default ConnectorPinsSection
